import React, { useEffect, useRef } from 'react'
import { Box, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import moment from 'moment'
import { Redirect } from 'react-router'

import { FormatDateTime } from 'src/date'
import { CustomTooltip, Loader } from 'src/designSystem'
import { calendarIcon, infoIcon } from 'src/designSystem/icons'
import OnlineStatus from 'src/legacy/components/OnlineStatus/OnlineStatus'
import RenderParticipantHistoryScoreGraph from 'src/legacy/components/ParticipantHistoryScore/RenderParticipantHistoryScoreGraph'
import PracticesPerWeek from 'src/legacy/components/PracticesPerWeek'
import ParticipantState from 'src/legacy/components/ParticipantState'
import { DailyTasks } from 'src/participant'
import QuickNote from 'src/participant/QuickNote'
import ParticipantActions from 'src/participant/ParticipantActions'
import { checkIfProductionEnvironment } from 'src/lib/checkIfProductionEnvironment'
import ProgramStartDateShift from 'src/participant/ParticipantInfo/ProgramStartDateShift'
import ProgramThemeInfo from 'src/participant/ParticipantInfo/ProgramThemeInfo'
import CurrentModuleGoal from 'src/participant/ParticipantInfo/CurrentModuleGoal'
import useLoadParticipantInfoData from 'src/participant/ParticipantInfo/useLoadParticipantInfoData'
import ParticipantTags from 'src/participant/ParticipantInfo/ParticipantTags'
import ParticipantQuestionnaires from 'src/participant/ParticipantInfo/ParticipantQuestionnaires'
import ParticipantSubtitleDetails from 'src/participant/ParticipantInfo/ParticipantSubtitleDetails'
import TechnicalDeviceDetails from './TechnicalDeviceDetails'
import CareTeam from '../CareTeam/CareTeam'
import { MedicalReport } from '../MedicalReport/MedicalReport'

type Props = {
  uid: string
}

const COACHING_PROGRAM_IDS = ['coaching', 'coaching_v2']

const languageDisplayNames = new Intl.DisplayNames(['en'], { type: 'language', fallback: 'none' })

const formatLanguage = (language?: string | null): string => {
  if (!language) return 'English'
  try {
    return languageDisplayNames.of(language) ?? 'English'
  } catch {
    return 'English'
  }
}

const useStyles = makeStyles({
  container: {
    height: '100%',
    overflowY: 'scroll',
    overflowX: 'hidden',
  },
  preferredName: {
    fontStyle: 'italic',
  },
})

const useHasBeenLoaded = (loading: boolean) => {
  const hasBeenLoadedRef = useRef<boolean>()

  useEffect(() => {
    if (!loading && hasBeenLoadedRef.current === undefined) {
      hasBeenLoadedRef.current = true
    }
  }, [loading])

  return hasBeenLoadedRef.current ?? false
}

const ParticipantInfo: React.FunctionComponent<Props> = (props) => {
  const { uid } = props

  const {
    acceptanceCriteria,
    accessCode,
    careTeam,
    createdAt,
    firstname,
    firstTimeLoginAt,
    programHasLabWork,
    isHrvProgram,
    lastname,
    lastWeekNbr,
    preferredName,
    programID,
    programStartDate,
    programEndMetrics,
    programLoaded,
    state,
    timezone,
    language,
    indicators: { completedResponses, engagement },
    loading,
    stateOfResidence,
    birthDate,
    hrvDevice,
    error,
  } = useLoadParticipantInfoData(uid)

  const classes = useStyles()

  const hasBeenLoaded = useHasBeenLoaded(loading)

  if (error) {
    return <Redirect to="/" />
  }

  // !hasBeenLoaded check is to prevent flickering when loading state changes but there's previous data available
  if (loading && !hasBeenLoaded) {
    return <Loader size="large" />
  }

  return (
    <Box className={classes.container} px={3} py={2}>
      <Box>
        <Box display="flex" alignItems="center">
          <Typography noWrap variant="h3">
            {firstname} {lastname}
          </Typography>
          {preferredName && (
            <CustomTooltip title="Preferred name">
              <Typography noWrap variant="h6" ml={1} className={classes.preferredName}>
                ({preferredName})
              </Typography>
            </CustomTooltip>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <img src={infoIcon} alt="" width={16} height={16} />
          <ParticipantSubtitleDetails timezone={timezone} birthDate={birthDate} stateOfResidence={stateOfResidence} />
        </Box>
        {programStartDate && (
          <Box display="flex" alignItems="center" gap={1}>
            <img src={calendarIcon} alt="" width={16} height={16} />
            <Typography noWrap variant="caption">
              Program start date: <strong>{moment.utc(programStartDate).format('l')}</strong>
            </Typography>
            {!checkIfProductionEnvironment() && (
              <ProgramStartDateShift
                uid={uid}
                programStartDate={programStartDate}
                lastWeekNbr={lastWeekNbr}
                timezone={timezone}
              />
            )}
          </Box>
        )}
        <OnlineStatus uid={uid} />
      </Box>

      <Box mt={2}>
        <MedicalReport uid={uid} />
      </Box>

      <Box mt={2}>
        <ParticipantTags uid={uid} />
      </Box>

      <Box mt={2}>
        <ParticipantQuestionnaires uid={uid} />
      </Box>

      {state && (
        <ParticipantActions
          uid={uid}
          engagement={engagement}
          acceptanceCriteria={acceptanceCriteria}
          programHasLabWork={programHasLabWork}
          isHrvProgram={isHrvProgram}
          lastWeekNbr={lastWeekNbr}
          programStartDate={programStartDate}
          programEndMetrics={programEndMetrics}
          programLoaded={programLoaded}
          state={state}
        />
      )}

      <Box mt={2} position="relative">
        <QuickNote uid={uid} />
      </Box>

      {programStartDate && programID && (
        <Box mt={2}>
          <ProgramThemeInfo uid={uid} programStartDate={programStartDate} programID={programID} timezone={timezone} />
        </Box>
      )}

      {programID && <DailyTasks uid={uid} programID={programID} />}

      <Box mt={2}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={0.35}>
          <Typography color="textSecondary" variant="h6">
            Practices by module
          </Typography>
          <CurrentModuleGoal uid={uid} />
        </Box>
        <PracticesPerWeek
          programStartDate={programStartDate!}
          completedResponses={completedResponses}
          lastWeekNbr={lastWeekNbr}
        />
      </Box>

      {(!programID || !COACHING_PROGRAM_IDS.includes(programID)) && (
        <>
          <Box mt={2}>
            <RenderParticipantHistoryScoreGraph uid={uid} type="PHQ9" programStartDate={programStartDate} />
          </Box>

          <Box mt={2}>
            <RenderParticipantHistoryScoreGraph uid={uid} type="GAD7" programStartDate={programStartDate} />
          </Box>
        </>
      )}

      <Box mt={2}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell>Careteam</TableCell>
              <TableCell>
                <CareTeam careTeam={careTeam} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>{state && <ParticipantState state={state} />}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Program ID</TableCell>
              <TableCell>{programID}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Access Code</TableCell>
              <TableCell>{accessCode}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Created At</TableCell>
              <TableCell>
                <FormatDateTime value={createdAt} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>First Login</TableCell>
              <TableCell>
                <FormatDateTime value={firstTimeLoginAt} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Timezone</TableCell>
              <TableCell>{timezone}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Language</TableCell>
              <TableCell>{formatLanguage(language)}</TableCell>
            </TableRow>
            <TechnicalDeviceDetails uid={uid} />
            {hrvDevice && (
              <TableRow>
                <TableCell>HRV Device</TableCell>
                <TableCell>{hrvDevice}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  )
}

export default ParticipantInfo
